import { parseContractEndDate, toAmericanDateShortString } from '@zspace/dates';
import { formatSalesOrderNumber } from '@zspace/format';
import {
  AdminDeviceTableRow,
  AdminLicenseKeyExpandableTableRow,
  AdminLicenseKeyTableRow,
  AdminSalesOrderTableRow,
} from '@zspace/types';
import Link from '../../ui/link/link';
import { Column } from '../../ui/table/types';

export const adminDevicesTableColumns: Column<AdminDeviceTableRow>[] = [
  {
    key: 'serialNumber.number',
    title: 'Serial number',
    className: 'is-width-20',
    render: (el) => {
      let deviceIdentifier = el.serialNumber?.number;
      if (!deviceIdentifier) {
        deviceIdentifier = el.name ?? el.defaultName;
      }
      return <Link to={`/devices/${el.id}`}>{deviceIdentifier}</Link>;
    },
    sortable: false,
  },
  {
    key: 'deviceGroup.name',
    title: 'Device group',
    className: 'is-width-20',
    render: (el) => el.deviceGroup?.name,
    sortable: true,
  },
  {
    key: 'name',
    title: 'Device name',
    className: 'is-width-10',
    render: (el) => el.name ?? el.defaultName,
  },
  {
    key: 'model.name',
    title: 'Device type',
    className: 'is-width-10',
    render: (el) => el.model?.displayName,
  },
  {
    key: 'registrationStatus',
    title: 'Status',
    className: 'is-width-5',
    render: (el) => el.registrationStatus,
  },
  {
    key: 'lastModifiedDate',
    title: 'Last modified date',
    className: 'is-width-15',
    render: (el) => {
      let date;

      if (el.lastModifiedDate) {
        date = toAmericanDateShortString(el.lastModifiedDate);
      }

      return date;
    },
  },
  {
    key: 'lastModifiedBy',
    title: 'Last modified by',
    className: 'is-width-20',
    render: (el) => el.lastModifiedBy,
  },
];

export const adminSalesOrdersTableColumns: Column<AdminSalesOrderTableRow>[] = [
  {
    key: 'number',
    title: 'Sales Order',
    className: 'is-width-20',
    render: (el) => (
      <Link to={`sales-orders/${el.id}`}>
        {formatSalesOrderNumber(el.number)}
      </Link>
    ),
    sortable: true,
  },
  {
    key: 'endUser',
    title: 'End user',
    className: 'is-width-35',
    render: (el) => el.endUser,
    sortable: true,
  },
  {
    key: 'softwareTitles',
    title: 'Software titles',
    className: 'is-width-15',
    render: (el) => el.softwareTitles,
    sortable: true,
  },
  {
    key: 'totalSeats',
    title: 'Total seats',
    className: 'is-width-15',
    render: (el) => el.totalSeats,
    sortable: true,
  },
  {
    key: 'totalDevices',
    title: 'Devices',
    className: 'is-width-15',
    render: (el) => el.totalDevices,
    sortable: true,
  },
];

export const adminLicenseKeyTableColumns = (
  isExpandableRow: (row: AdminLicenseKeyTableRow) => boolean
): Column<AdminLicenseKeyTableRow, AdminLicenseKeyExpandableTableRow>[] => [
  {
    key: 'softwareTitle',
    title: 'Software title',
    className: 'is-width-30',
    render: (el) => (
      <Link to={`software/${el.licensingProductGroupId}`}>
        {el.softwareTitle}
      </Link>
    ),
    sortable: true,
  },
  {
    key: 'licenseKey',
    title: 'License key',
    className: 'is-width-30',
    render: (el) => {
      if (isExpandableRow(el)) {
        return null;
      }
      return el.licenseKey;
    },
    expandableContentRender: (el) => el.licenseKey,
    sortable: false,
  },
  {
    key: 'assignedSeats',
    title: 'Assigned seats',
    className: 'is-width-10',
    render: (el) => el.assignedSeats,
    expandableContentRender: (el) => el.assignedSeats,
    sortable: true,
  },
  {
    key: 'unassignedSeats',
    title: 'Unassigned seats',
    className: 'is-width-10',
    render: (el) => el.unassignedSeats,
    expandableContentRender: (el) => el.unassignedSeats,
    sortable: true,
  },
  {
    key: 'totalSeats',
    title: 'Total seats',
    className: 'is-width-10',
    render: (el) => el.totalSeats,
    expandableContentRender: (el) => el.totalSeats,
    sortable: true,
  },
  {
    key: 'contractEndDate',
    title: 'Contract end date',
    className: 'is-width-10',
    render: (el) => parseContractEndDate(el.contractEndDate),
    sortable: true,
  },
];
